/**
 * Guards against a well-documented crash class where a browser page-translate feature (Google
 * Translate on Chrome and its equivalents) rewrites live text nodes into <font> wrappers behind
 * React's back. When React later reconciles that subtree (a conditional insert/remove, a list
 * re-render, a state-driven mount/unmount) it can call removeChild/insertBefore against a node
 * translate has already detached or reparented, throwing "Failed to execute 'removeChild' on
 * 'Node': The node to be removed is not a child of this node" — which is uncaught by React and
 * tears down the whole page. See https://github.com/facebook/react/issues/11538.
 *
 * Patches the two DOM methods to degrade gracefully instead of throwing when the DOM no longer
 * matches what the caller expects, so a translate-induced mismatch becomes a no-op rather than a
 * page crash. Idempotent and safe to call from multiple entry points.
 */
export function installTranslateSafeDomPatches(): void {
  if (typeof window === 'undefined' || typeof Node === 'undefined' || !Node.prototype) return;

  const win = window as typeof window & { __translateSafeDomPatchesInstalled__?: boolean };
  if (win.__translateSafeDomPatchesInstalled__) return;
  win.__translateSafeDomPatchesInstalled__ = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
